import React, { useState, useRef, useEffect } from "react";

const Calculator = ({
  title = "Calculator",
  inputs = [],
  outputs = [],
  calculateFunction,
  printScheduleFunction,
}) => {
  const calculatorRef = useRef(null);

  // Simple state object with input values
  const [inputsState, setInputsState] = useState(() => {
    const initial = {};
    inputs.forEach(input => {
      if (input.format === "radio") {
        // Radio buttons will be false by default, unless there's a `placeholder`
        initial[input.group] = input.placeholder ? input.id : false;
      } else {
        initial[input.id] = input.placeholder ?? 0;
      }
    });
    return initial;
  });

  // used by Amortization calculator
  const [groupPaymentsByYear, setGroupPaymentsByYear] = useState(false);

  // Calculate outputs whenever state changes
  const calculatedOutputs = calculateFunction
    ? calculateFunction(inputsState)
    : {};

  // Format values based on type
  const formatValue = (value, format) => {
    switch (format) {
      case "currency":
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(value || 0);
      case "currencyWhole":
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      case "percentage":
        return new Intl.NumberFormat("en-US", {
          style: "percent",
          minimumFractionDigits: 2,
        }).format((value || 0) / 100);
      case "date":
        return new Date(value || 0).toLocaleDateString("en-US");
      case "number":
      default:
        return new Intl.NumberFormat("en-US").format(value || 0);
    }
  };

  // Handle input changes
  const handleInputChange = (input, event) => {
    let key = input.format === "radio" ? input.group : input.id;
    if (input.format !== "radio") {
      let validationElm = event.target
        .closest(".input-field")
        .querySelector(".calculator__validation-error");
      // If this change results in an empty value,
      // and the previous value was NOT empty (user had entered something),
      // show the validation error.
      if (event.target.value === "" && inputsState[key] !== 0) {
        validationElm.classList.add("is-visible");
      } else {
        validationElm.classList.remove("is-visible");
      }
      // prevent negative values
      event.target.value = Math.abs(event.target.value);
    }

    setInputsState(prev => ({
      ...prev,
      [key]:
        input.format !== "radio"
          ? parseFloat(event.target.value) || 0
          : event.target.value,
    }));
  };

  useEffect(() => {
    // fire printScheduleFunction in Amortization calculators
    if (printScheduleFunction) {
      printScheduleFunction(calculatorRef.current, groupPaymentsByYear);
    }
  }, [inputsState, groupPaymentsByYear]);

  const renderFieldGroupHeading = input => (
    <h3 key={input.id} className="calculator__input-heading">
      {input.title}
    </h3>
  );

  const renderInputField = input => (
    <div key={input.id} className="input-field">
      {input.format !== "radio" && (
        <span className="calculator__input-label" htmlFor={`field-${input.id}`}>
          {input.title}
        </span>
      )}
      <div
        className={`calculator__input-wrapper ${input.format === "radio" ? "calculator__input-wrapper--radio" : ""}`}
      >
        {input.format !== "radio" && (
          <div className="calculator__input-symbol">
            {input.format === "currency" ? "$" : ""}
            {input.format === "percentage" ? "%" : ""}
          </div>
        )}
        <input
          type={
            input.format === "date"
              ? "date"
              : input.format === "radio"
                ? "radio"
                : "number"
          }
          placeholder={input.placeholderText ?? "0"}
          onChange={e => handleInputChange(input, e)}
          className="calculator__input"
          name={input.format === "radio" ? input.group : input.id}
          {...(input.format === "radio" && { value: input.id })}
          {...(input.format === "radio" && {
            checked: inputsState[input.group] === input.id,
          })}
          id={`field-${input.id}`}
        />
        {input.format === "radio" && (
          <label
            className="calculator__input-label"
            htmlFor={`field-${input.id}`}
          >
            {input.title}
          </label>
        )}
      </div>
      <div className="calculator__validation-error">Fill out this field</div>
    </div>
  );

  return (
    <div ref={calculatorRef} className="calculator">
      <form className="calculator__inputs">
        {inputs.map(input => {
          // When condition exists, only render headings/fields if condition is met
          if (
            input.conditionKey === undefined ||
            inputsState[input.conditionKey] === input.conditionValue
          ) {
            return input.format === "heading"
              ? renderFieldGroupHeading(input)
              : renderInputField(input);
          }
        })}
      </form>

      <div className="calculator__outputs">
        <h3 className="calculator__output-title">Summary</h3>
        <div className="calculator__output-fields">
          {outputs.map(output => (
            <div key={output.id} className="calculator__output-field">
              <span className="calculator__output-label">{output.title}</span>
              <span className="calculator__output-value">
                {formatValue(calculatedOutputs[output.id], output.format)}
              </span>
            </div>
          ))}
        </div>
        {/* Payments table, used by Amortization calculator */}
        <div className="payments-table js-payments-outer">
          <div className="payments-table__heading">
            <h3>Payments</h3>
            <div className="payments-table__heading-actions">
              <button onClick={() => window.print()}>Print</button>
              <div className="payments-table__toggle-payment-schedule">
                <input
                  type="radio"
                  name="schedule"
                  id="schedule-0"
                  value="Monthly"
                  checked={!groupPaymentsByYear}
                />
                <label
                  htmlFor="schedule-0"
                  className="field-label"
                  onClick={() => setGroupPaymentsByYear(false)}
                >
                  Monthly
                </label>
                <input
                  type="radio"
                  name="schedule"
                  id="schedule-1"
                  value="Yearly"
                  checked={groupPaymentsByYear}
                />
                <label
                  htmlFor="schedule-1"
                  className="field-label"
                  onClick={() => setGroupPaymentsByYear(true)}
                >
                  Yearly
                </label>
              </div>
            </div>
          </div>
          <table className="table-amortization">
            <thead>
              <tr>
                <th>Payment Date</th>
                <th>Principal Paid</th>
                <th>Interest Paid</th>
                <th>Remaining Balance</th>
              </tr>
            </thead>
            <tbody>
              {/* Amortization schedule rows are dynamically added here */}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
